import { API_BASE_URL } from '../config/environment';
import type { Feedback } from '../types/feedback';

interface FeedbackErrorResponse {
  error?: unknown;
  message?: unknown;
}

export class FeedbackApiService {
  async submitFeedback(message: string, email?: string): Promise<Feedback> {
    const response = await fetch(`${API_BASE_URL}/feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-idempotency-key': crypto.randomUUID(),
      },
      body: JSON.stringify({
        message,
        email: email?.trim() || undefined,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch((): FeedbackErrorResponse => ({}));
      throw new Error(getErrorMessage(errorData, response.status));
    }

    const data: unknown = await response.json();
    if (!isFeedbackResponse(data)) {
      throw new Error('Invalid feedback response');
    }
    return data.feedback;
  }
}

function getErrorMessage(errorData: FeedbackErrorResponse, status: number): string {
  if (typeof errorData.error === 'string') return errorData.error;
  if (typeof errorData.message === 'string') return errorData.message;
  return `Failed to submit feedback (${status})`;
}

function isFeedbackResponse(value: unknown): value is { feedback: Feedback } {
  return isRecord(value) && isFeedback(value.feedback);
}

function isFeedback(value: unknown): value is Feedback {
  if (!isRecord(value)) return false;
  return typeof value.id === 'string'
    && typeof value.message === 'string'
    && (value.email === undefined || typeof value.email === 'string')
    && (value.status === 'pending' || value.status === 'accepted' || value.status === 'rejected')
    && typeof value.createdAt === 'string'
    && typeof value.updatedAt === 'string'
    && (value.reviewedAt === undefined || typeof value.reviewedAt === 'string')
    && (value.reviewedBy === undefined || typeof value.reviewedBy === 'string');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

export const feedbackApi = new FeedbackApiService();
